import DOMPurify from 'dompurify';

import iranSansBold from '/fonts/IRANSans/woff2/IRANSansWeb-FaNum_Bold.woff2?url&inline';
import iranSansLight from '/fonts/IRANSans/woff2/IRANSansWeb-FaNum_Light.woff2?url&inline';
import iranSansMedium from '/fonts/IRANSans/woff2/IRANSansWeb-FaNum_Medium.woff2?url&inline';
import iranSansUltraLight from '/fonts/IRANSans/woff2/IRANSansWeb-FaNum_UltraLight.woff2?url&inline';
import iranSansRegular from '/fonts/IRANSans/woff2/IRANSansWeb-FaNum.woff2?url&inline';

// Reuse the project's font files without network requests from the opaque sandbox.
const fontFaces = [
  [200, iranSansUltraLight],
  [300, iranSansLight],
  [400, iranSansRegular],
  [500, iranSansMedium],
  [700, iranSansBold]
]
  .map(
    ([weight, src]) =>
      `@font-face { font-family: IRANSans; font-style: normal; font-weight: ${weight}; src: url("${src}") format("woff2"); }`
  )
  .join('\n');

type TagDescriptionTheme = {
  borderColor: string;
  headerBackground: string;
};

export const createTagDescriptionDocument = (
  html: string,
  theme: TagDescriptionTheme = {borderColor: 'lightgray', headerBackground: 'whitesmoke'}
): string => {
  // Only presentation markup is needed. In particular, no URL-bearing attributes,
  // forms, embedded documents, SVG/MathML, or operator-supplied document metadata.
  const content = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'a',
      'abbr',
      'b',
      'blockquote',
      'br',
      'caption',
      'code',
      'col',
      'colgroup',
      'dd',
      'del',
      'details',
      'div',
      'dl',
      'dt',
      'em',
      'figcaption',
      'figure',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'hr',
      'i',
      'li',
      'ol',
      'p',
      'pre',
      's',
      'section',
      'small',
      'span',
      'strong',
      'style',
      'sub',
      'summary',
      'sup',
      'table',
      'tbody',
      'td',
      'th',
      'thead',
      'tfoot',
      'tr',
      'u',
      'ul'
    ],
    ALLOWED_ATTR: [
      'align',
      'bgcolor',
      'border',
      'bordercolor',
      'cellpadding',
      'cellspacing',
      'class',
      'colspan',
      'dir',
      'frame',
      'height',
      'id',
      'lang',
      'nowrap',
      'open',
      'rowspan',
      'rules',
      'scope',
      'span',
      'start',
      'style',
      'title',
      'valign',
      'width'
    ],
    ALLOW_ARIA_ATTR: false,
    ALLOW_DATA_ATTR: false,
    FORCE_BODY: true
  });

  // Inline CSS preserves backoffice formatting; all network resources stay blocked,
  // including CSS imports, background images and remote fonts. Only embedded fonts
  // are permitted, so the sandbox does not need same-origin access.
  const policy =
    "default-src 'none'; script-src 'none'; style-src 'unsafe-inline'; font-src data:; base-uri 'none'; form-action 'none'";

  // A srcdoc is a separate document and cannot inherit the shell's RTL direction.
  return `<!doctype html>
<html lang="fa" dir="rtl">
<head>
<meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${policy}">
<meta name="referrer" content="no-referrer">
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>${fontFaces}</style>
<style>
/* The backoffice editor's content styles are not included in its exported HTML.
   Zero-specificity defaults let authored styles and presentation attributes win. */
:where(p) { margin: 0 0 8px; min-height: 1.6em; }
:where(table) { margin-bottom: 8px; table-layout: auto; }
:where(table:not([width])) { width: auto; }
:where(table:not([cellspacing])) { border-collapse: collapse; }
:where(th, td) { min-width: 80px; word-break: break-word; position: relative; }
:where(table:not([border]) th, table:not([border]) td) { border: 1px solid ${theme.borderColor}; }
:where(table:not([cellpadding]) th, table:not([cellpadding]) td) { padding: 8px; }
:where(th:not([valign]), td:not([valign])) { vertical-align: top; }
:where(th:not([bgcolor])) { background-color: ${theme.headerBackground}; }
:where(th) { font-weight: 600; }
</style>
</head>
<body>${content}
<style>
html { overflow: auto !important; }
body { margin: 0; padding: 0; box-sizing: border-box; }
body, body * { font-family: IRANSans, system-ui, sans-serif !important; }
</style>
</body>
</html>`;
};
